import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { Copy, ShieldCheck, ArrowLeft, Loader2, Receipt } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://tn.server.goatteachers.com/api";
const DEFAULT_BKASH = "01323719131";

export default function Checkout() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const isTutor = user?.role === "tutor";
  const packId = params.get("packId");

  const [wallet, setWallet] = useState({
    credits: 0,
    free_unlock_used: false,
    pending_topups: 0,
  });

  const [packs, setPacks] = useState([]);
  const [senderNumber, setSenderNumber] = useState("");
  const [topupInstructions, setTopupInstructions] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const authHeaders = useMemo(() => {
    const token = user?.token || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  // hard guard
  useEffect(() => {
    const token = localStorage.getItem("token");

    // 1) Not logged in at all
    if (!token && !user) {
      navigate("/tutor-submission", { replace: true });
      return;
    }

    // 2) Token exists but context not hydrated yet -> DO NOTHING (wait)
    if (token && !user) return;

    // 3) Now we have a user -> enforce tutor-only
    if (user?.role !== "tutor") {
      navigate("/tutor-submission", { replace: true });
    }
  }, [user, navigate]);

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/me`, {
        headers: { ...authHeaders },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setWallet({
          credits: Number(data.credits || 0),
          free_unlock_used: !!data.free_unlock_used,
          pending_topups: Number(data.pending_topups || 0),
        });
      }
    } catch (e) {
      // silent
    }
  };

  const fetchPacks = async () => {
    const res = await fetch(`${API_BASE}/tutors/wallet/packs`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Failed to load packs.");
    return Array.isArray(data.packs) ? data.packs : [];
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!isTutor) return;
      setLoading(true);
      setMsg("");
      try {
        const [packsList] = await Promise.all([fetchPacks(), fetchWallet()]);
        if (!alive) return;
        setPacks(packsList);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Server error loading checkout.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutor]);

  const selectedPack = useMemo(
    () => packs.find((p) => Number(p.id) === Number(packId)),
    [packs, packId],
  );

  const packName =
    selectedPack?.name ||
    selectedPack?.title ||
    selectedPack?.pack_name ||
    selectedPack?.credit_pack_name ||
    (selectedPack?.id ? `Pack #${selectedPack.id}` : "");

  const payTo = topupInstructions?.pay_to || DEFAULT_BKASH;
  const amount = selectedPack?.price_bdt;

  const copyText = async (t) => {
    try {
      await navigator.clipboard?.writeText(String(t));
      setMsg(" Copied to clipboard.");
      setTimeout(() => setMsg(""), 1200);
    } catch {
      setMsg("Could not copy.");
      setTimeout(() => setMsg(""), 1200);
    }
  };

  const submitTopupRequest = async () => {
    if (!selectedPack?.id) {
      toast.error("Please select a pack again.");
      return;
    }

    if (!senderNumber?.trim()) {
      toast.error("Sender bKash number is required.");
      return;
    }

    setSubmitting(true);
    setMsg("");

    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/topup-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({
          pack_id: selectedPack.id,
          sender_number: senderNumber || null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Failed to submit request.");
        return;
      }

      setTopupInstructions(data?.instructions || null);

      toast.success(
        `Request submitted! Please pay to ${
          data?.instructions?.pay_to || DEFAULT_BKASH
        } — wait for admin approval.`,
        { autoClose: 3500 },
      );

      await fetchWallet();

      // Optional: clear input after submit
      // setSenderNumber("");

      // Optional: navigate away after submit
      setTimeout(() => {
        navigate("/tutor/profile");
        setTimeout(
          () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" }),
          0,
        );
      }, 800);
    } catch (e) {
      toast.error("Server error submitting request.");
    } finally {
      setSubmitting(false);
    }
  };

  const token = localStorage.getItem("token");
  if (token && !user) return null; // wait for hydration
  if (!user || user.role !== "tutor") return null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <ToastContainer
        position="top-right"
        theme="light"
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <div className="mx-auto max-w-5xl px-4 py-10">
        {/* top bar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <button
            type="button"
            onClick={() => navigate("/packages")}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-950"
          >
            <ArrowLeft size={18} /> Back
          </button>

          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200 px-3 py-1 text-sm">
            <ShieldCheck size={16} />
            Tutor secure checkout
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* left */}
          <div className="lg:col-span-2 rounded-3xl bg-white ring-2 ring-slate-200 p-6 shadow-sm">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              Checkout
            </h1>
            <p className="mt-2 text-slate-600">
              Send money via bKash and submit your payment info for
              verification.
            </p>

            {loading && (
              <div className="mt-6 inline-flex items-center gap-2 text-slate-600">
                <Loader2 className="animate-spin" size={18} />
                Loading…
              </div>
            )}

            {!loading && !selectedPack && (
              <div className="mt-6 rounded-2xl bg-red-50 ring-2 ring-red-200 p-4 text-red-700">
                Invalid pack selected. Please go back and choose again.
              </div>
            )}

            {!loading && selectedPack && (
              <>
                {/* payment card */}
                <div className="mt-6 rounded-3xl bg-slate-50 ring-2 ring-slate-200 p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-sm text-slate-500">
                        Selected package
                      </div>
                      <div className="text-xl font-semibold text-slate-950">
                        {packName} • <br /> {selectedPack.credits} Applications
                      </div>

                      <div className="mt-1 text-slate-600">
                        Amount: <b className="text-slate-950">৳ {amount}</b>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-white ring-2 ring-slate-200 px-4 py-3">
                      <div className="text-sm text-slate-700">
                        Applications Left:{" "}
                        <b className="text-slate-950">{wallet.credits}</b>
                      </div>
                      {/* <div className="text-sm text-slate-700">
                        Pending: <b className="text-slate-950">{wallet.pending_topups}</b>
                      </div> */}
                    </div>
                  </div>
                </div>

                {/* bkash info */}
                <div className="mt-4 rounded-3xl bg-white ring-2 ring-slate-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-2 text-slate-800 font-semibold">
                      <Receipt size={18} />
                      bKash (Send Money Only)
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-slate-50 ring-2 ring-slate-200 p-4">
                      <div className="text-xs text-slate-500">Pay to</div>
                      <div className="mt-1 text-lg font-semibold text-slate-950">
                        {payTo}
                      </div>
                      <button
                        type="button"
                        onClick={() => copyText(payTo)}
                        className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-100 hover:bg-slate-200 px-3 py-2 text-sm text-slate-800"
                      >
                        <Copy size={16} /> Copy number
                      </button>
                    </div>

                    <div className="rounded-2xl bg-slate-50 ring-2 ring-slate-200 p-4">
                      <div className="text-xs text-slate-500">Amount</div>
                      <div className="mt-1 text-lg font-semibold text-slate-950">
                        ৳ {amount}
                      </div>
                      <button
                        type="button"
                        onClick={() => copyText(amount)}
                        className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-100 hover:bg-slate-200 px-3 py-2 text-sm text-slate-800"
                      >
                        <Copy size={16} /> Copy amount
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 text-sm text-slate-600">
                    Note: Server verifies payment and then activates package.
                  </div>
                </div>

                {/* sender */}
                <div className="mt-4 rounded-3xl bg-white ring-2 ring-slate-200 p-5 shadow-sm">
                  <div className="text-sm font-semibold text-slate-800">
                    Sender bKash number (mandatory)
                  </div>

                  <input
                    value={senderNumber}
                    onChange={(e) => setSenderNumber(e.target.value)}
                    placeholder="01XXXXXXXXX"
                    className="mt-3 w-full rounded-2xl bg-white ring-2 ring-slate-200 px-4 py-3 text-slate-950 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                  <div className="mt-2 text-xs text-slate-500">
                    Use the number you paid from to speed up verification.
                  </div>

                  <button
                    type="button"
                    disabled={submitting}
                    onClick={submitTopupRequest}
                    className={[
                      "mt-4 w-full rounded-2xl px-5 py-3 font-semibold transition inline-flex items-center justify-center gap-2",
                      submitting
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                        : "bg-emerald-500 text-white hover:bg-emerald-600",
                    ].join(" ")}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Submitting…
                      </>
                    ) : (
                      "Confirm Payment"
                    )}
                  </button>

                  {msg && (
                    <div className="mt-3 text-sm text-slate-700">{msg}</div>
                  )}

                  {topupInstructions?.note && (
                    <div className="mt-2 text-xs text-slate-500">
                      {topupInstructions.note}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* right: trust / steps */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 rounded-3xl bg-white ring-2 ring-slate-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-950">
                How it works
              </h3>
              <ol className="mt-4 space-y-3 text-sm text-slate-600 leading-relaxed">
                <li className="rounded-2xl bg-slate-50 ring-2 ring-slate-200 p-4">
                  1) Select a package
                </li>
                <li className="rounded-2xl bg-slate-50 ring-2 ring-slate-200 p-4">
                  2) bKash Send Money to the provided number
                </li>
                <li className="rounded-2xl bg-slate-50 ring-2 ring-slate-200 p-4">
                  3) Click “Confirm Payment” to submit for payment verification
                </li>
              </ol>

              {!wallet.free_unlock_used && (
                <div className="mt-4 rounded-2xl bg-emerald-50 ring-2 ring-emerald-200 p-4 text-emerald-700 text-sm">
                  You have 1 free application available (no need to buy now)
                  from Tutor Network.
                </div>
              )}

              <div className="mt-4 text-xs text-slate-500">
                For safety: we never expose tutor packages to students.
              </div>
            </div>
          </div>
        </div>

        {/* footer back */}
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate("/tuition-board")}
            className="text-sm text-slate-600 hover:text-slate-950 underline underline-offset-4"
          >
            Back to Tuition Board
          </button>
        </div>
      </div>
    </div>
  );
}
